import { poisonRecordKey, type DurableItemIdentity, type DurablePoisonRecord } from "./durableUploadSchema";
import type { DurableOwnershipPort, DurableUploadTransaction } from "./ports";
import { R0_SLOW_TIER_BASE_DELAY_MS, R0_SLOW_TIER_MAX_DELAY_MS } from "./retryPolicy";

/**
 * Durable, item-scoped admission/retry budget shared by every upload lane.
 *
 * The recording chunk stream and the small-file lane each carried a verbatim copy of this adapter,
 * differing only in where a poison-port failure is reported (lane telemetry vs. the small-file
 * `onDrainError` hook) and in the ceiling each lane falls back to. Both are injected here.
 *
 * ## R-0 (owner directive 2026-08-26): pacing, not parking
 *
 * The budget keeps its ONE legitimate job — stopping a repeatedly failing item from hot-looping and
 * starving its healthy siblings for the lane's active slots — but its verdict changed. Reaching the
 * ceiling used to mean "this item is no longer scheduler work", which is an attempt-count-derived
 * terminal state and is exactly what R-0 abolishes. It now means "this item moves to the slow
 * backoff tier": it is deferred for a 30-second window and then becomes eligible
 * again, forever, until either it succeeds or genuine EVIDENCE (bytes gone, 409 UPLOAD_LOST, a
 * byte-integrity/receipt-identity mismatch, revoked auth) terminalizes it.
 *
 * The durable failure count is still recorded, still cleared on success, and still consulted before
 * dispatch — only the consequence of exceeding it changed.
 */
export interface PoisonBudgetConfig<TRecord> {
	originKey: string;
	identity: (record: TRecord) => DurableItemIdentity;
	getOwnership: () => Promise<DurableOwnershipPort | undefined>;
	ceiling?: number;
}

/** Which poison-port call failed, so a lane can map it onto its own telemetry vocabulary. */
export type PoisonBudgetPhase = "admission" | "record" | "clear";

/** A repeatedly failing item waits 30 seconds; failures below the ceiling add no delay. */
export function computePoisonPacingDelayMs(failureCount: number, ceiling: number): number {
	if (!Number.isFinite(failureCount) || !Number.isFinite(ceiling)) return R0_SLOW_TIER_MAX_DELAY_MS;
	return Math.floor(failureCount) < Math.max(1, Math.floor(ceiling)) ? 0 : R0_SLOW_TIER_MAX_DELAY_MS;
}

/**
 * The wall-clock instant an over-ceiling item becomes schedulable again, expressed against the
 * READER's clock rather than the writer's.
 *
 * Break-it round 1 (D5): this used to be `lastFailureAt + delay` with no relation to now at all. A
 * durable poison record written while the device clock was ahead — an NTP correction, a user changing
 * the clock, a profile restored from a skewed machine — therefore paced the item for as long as the
 * skew lasted: days, or a year. That is an unschedulable row, i.e. the attempt-derived park R-0
 * removed, resurrected through the clock. Two clamps, in the spirit of `durableOwnership`'s
 * `assertClockSafe`:
 *
 *  - never further than the 30-second ceiling from now, whatever the record claims;
 *  - a window that has ALREADY elapsed (including one written by a clock that was behind) paces
 *    nothing, and says so as `0` — "not paced", the same answer a below-ceiling record gives.
 *
 * `0` is deliberate rather than `nowMs`: the value is compared as `nowMs >= pacedUntil` and is also
 * handed to schedulers as "wake me then", so an elapsed window must read as unambiguously past on
 * every clock, not merely on the one that produced it.
 */
export function computePoisonPacedUntil(record: DurablePoisonRecord, nowMs = Date.now()): number {
	const delayMs = computePoisonPacingDelayMs(record.failureCount, record.ceiling);
	if (delayMs <= 0) return 0;
	const now = Number.isFinite(nowMs) ? nowMs : Date.now();
	const lastFailureAt = Number.isFinite(record.lastFailureAt) ? record.lastFailureAt : now;
	const pacedUntil = lastFailureAt + delayMs;
	if (!Number.isFinite(pacedUntil) || pacedUntil <= now) return 0;
	return Math.min(pacedUntil, now + R0_SLOW_TIER_MAX_DELAY_MS);
}

/** One `filterEligible` pass's durable `readPoisonFailure` reads, keyed by poison record key. */
type PoisonPassReads = Map<string, Promise<DurablePoisonRecord | undefined>>;

/** What the durable budget currently says about one item. */
export interface PoisonPacingVerdict {
	/** False only while the item is inside its current pacing window; never a terminal answer. */
	eligible: boolean;
	/** Present when the durable budget could be read; absent when the port is unavailable. */
	failureCount?: number;
	ceiling?: number;
	/** The instant this item becomes eligible again, when it is currently paced. */
	pacedUntil?: number;
}

export interface PoisonBudget<TRecord> {
	/**
	 * Drops records currently inside their pacing window; a failed admission read also excludes the
	 * record from THIS pass (a later wake retries the read). No record is ever excluded permanently.
	 */
	/** `onPacedUntil` also receives the paced record, so a caller's wake diagnostics can name the row (LSP-20948). */
	filterEligible(records: TRecord[], nowMs?: number, onPacedUntil?: (pacedUntil: number, record: TRecord) => void): Promise<TRecord[]>;
	/** True while the item is inside its pacing window. R-0: paced, not parked — this always expires. */
	isPaced(record: TRecord, nowMs?: number, onPacedUntil?: (pacedUntil: number) => void): Promise<boolean>;
	/** Only ever charged inside a writer-fenced transaction. */
	recordFailure(record: TRecord, error: unknown, transaction?: DurableUploadTransaction): Promise<DurablePoisonRecord | undefined>;
	/** Best-effort cleanup after a durable completion; never turns success into a retry. */
	clearFailure(record: TRecord, transaction?: DurableUploadTransaction): Promise<void>;
	getOwnership(): Promise<DurableOwnershipPort | undefined>;
}

export function createPoisonBudget<TRecord>(options: {
	config: PoisonBudgetConfig<TRecord> | undefined;
	/** Used when the config supplies no explicit ceiling. Read lazily: lanes resolve it per attempt. */
	defaultCeiling: () => number;
	now: () => number;
	onError: (phase: PoisonBudgetPhase, record: TRecord | undefined, error: unknown) => void;
}): PoisonBudget<TRecord> {
	const { config, defaultCeiling, now, onError } = options;

	/**
	 * Break-it round 1 (D3/D6): the two in-memory fallbacks, both bounded and both per lane instance.
	 *
	 *  - `memoryFailures` is the pacing counter used when the DURABLE port cannot be built at all
	 *    (`getOwnership()` throwing is the realistic degraded-storage case: IndexedDB blocked in a
	 *    private window, or mid-reopen). `ownershipOrUndefined` used to swallow that and answer
	 *    "eligible", which — with `retryRefreshImmediately` and R-0's no-attempt-terminal rule — left
	 *    NOTHING bounding the refresh self-rearm loop: an unbounded zero-delay presign+PUT storm.
	 *  - `legacyPacingStartedAt` is the first instant a boolean-only `isPoisoned` port reported an item
	 *    over its ceiling. Without it the legacy branch recomputed `now + BASE` on every read, so the
	 *    window could never elapse and the item was PARKED, not deferred — the docstring said the
	 *    opposite and the code could not deliver it.
	 *
	 * Neither survives a reload; both only have to bound one page's worth of retries, and the durable
	 * budget takes over the moment the port is readable again.
	 */
	const MEMORY_PACING_MAX_ITEMS = 256;
	const memoryFailures = new Map<string, DurablePoisonRecord>();
	const legacyPacingStartedAt = new Map<string, number>();

	function itemKey(record: TRecord): string {
		const identity = config!.identity(record);
		return `${config!.originKey}:${identity?.itemId ?? ""}:${identity?.takeId ?? ""}`;
	}

	/** Keeps either fallback map from growing without bound on a long-lived tab. */
	function boundMap(map: Map<string, unknown>): void {
		while (map.size > MEMORY_PACING_MAX_ITEMS) {
			const oldest = map.keys().next();
			if (oldest.done) return;
			map.delete(oldest.value);
		}
	}

	function memoryVerdict(record: TRecord, nowMs: number): PoisonPacingVerdict {
		const existing = memoryFailures.get(itemKey(record));
		if (!existing) return { eligible: true };
		const pacedUntil = computePoisonPacedUntil(existing, nowMs);
		return {
			eligible: nowMs >= pacedUntil,
			failureCount: existing.failureCount,
			ceiling: existing.ceiling,
			pacedUntil
		};
	}

	function noteMemoryFailure(record: TRecord, error: unknown): DurablePoisonRecord {
		const key = itemKey(record);
		const identity = config!.identity(record);
		const previous = memoryFailures.get(key);
		const next: DurablePoisonRecord = {
			key,
			itemId: identity?.itemId ?? "",
			takeId: identity?.takeId,
			failureCount: (previous?.failureCount ?? 0) + 1,
			ceiling: config!.ceiling ?? defaultCeiling(),
			lastFailureAt: now(),
			lastReason: error instanceof Error ? error.message : String(error)
		};
		memoryFailures.delete(key);
		memoryFailures.set(key, next);
		boundMap(memoryFailures);
		return next;
	}

	async function ownershipOrUndefined(): Promise<DurableOwnershipPort | undefined> {
		if (!config) return undefined;
		try {
			return await config.getOwnership();
		} catch (error) {
			// Silent-failure rule: this is the fail-condition that used to answer "eligible" and let the
			// lane hot-loop. It still degrades, but to the bounded in-memory budget above, and it still
			// says which phase failed.
			onError("admission", undefined, error);
			return undefined;
		}
	}

	/**
	 * One durable read, translated into R-0's pacing verdict. `readPoisonFailure` is preferred
	 * because it carries the failure count and last-failure instant the pacing window is computed
	 * from; a port that only implements the older boolean `isPoisoned` degrades to a deferral anchored
	 * at the first over-ceiling observation, which is still a deferral rather than a park.
	 */
	async function readVerdict(record: TRecord, nowMs: number, passReads?: PoisonPassReads): Promise<PoisonPacingVerdict> {
		if (!config) return { eligible: true };
		const ownership = await ownershipOrUndefined();
		// No durable port: the in-memory budget is the only thing bounding this item's retries.
		if (!ownership) return memoryVerdict(record, nowMs);
		if (typeof ownership.readPoisonFailure === "function") {
			// LSP-22557: the recording lane's identity is per TAKE, so one pass over N chunks of a take
			// asked IndexedDB the same question N times. This branch is a pure read, so the pass shares
			// one read (a rejection included) per poison key. The legacy `isPoisoned` branch below is
			// deliberately not shared: each call can re-anchor its window, which siblings must observe.
			const identity = config.identity(record);
			const passKey = passReads && poisonRecordKey(config.originKey, identity);
			let poisonRead = passKey ? passReads.get(passKey) : undefined;
			if (!poisonRead) {
				poisonRead = ownership.readPoisonFailure(config.originKey, identity);
				if (passKey) passReads.set(passKey, poisonRead);
			}
			const poisonRecord = await poisonRead;
			if (!poisonRecord) return { eligible: true };
			const pacedUntil = computePoisonPacedUntil(poisonRecord, nowMs);
			return {
				eligible: nowMs >= pacedUntil,
				failureCount: poisonRecord.failureCount,
				ceiling: poisonRecord.ceiling,
				pacedUntil
			};
		}
		if (typeof ownership.isPoisoned !== "function") return { eligible: true };
		const key = itemKey(record);
		const overCeiling = await ownership.isPoisoned(config.originKey, config.identity(record));
		if (!overCeiling) {
			legacyPacingStartedAt.delete(key);
			return { eligible: true };
		}
		// Deferral, not park: anchor the window at the FIRST observation so it actually elapses. A
		// recomputed `now + BASE` on every read is a window that can never end.
		let startedAt = legacyPacingStartedAt.get(key);
		if (startedAt === undefined || startedAt > nowMs) {
			startedAt = nowMs;
			legacyPacingStartedAt.set(key, startedAt);
			boundMap(legacyPacingStartedAt);
		}
		const pacedUntil = startedAt + R0_SLOW_TIER_BASE_DELAY_MS;
		if (nowMs >= pacedUntil) {
			// The window elapsed. Re-admit the item and re-anchor, so a still-poisoned item is paced
			// again for one more window rather than being either parked or hot-looped.
			legacyPacingStartedAt.set(key, nowMs);
			return { eligible: true, pacedUntil };
		}
		return { eligible: false, pacedUntil };
	}

	async function guardedVerdict(record: TRecord, nowMs: number, passReads?: PoisonPassReads): Promise<PoisonPacingVerdict> {
		try {
			return await readVerdict(record, nowMs, passReads);
		} catch (error) {
			// The read failed, so nothing is known about this item's budget. Skip it for THIS pass
			// only (a broken admission read must not dispatch an item the ceiling may already be
			// pacing) and hand back a window so the caller still schedules a follow-up wake. A read
			// shared across a take lands here once per record, so each skipped row is still reported.
			onError("admission", record, error);
			return {
				eligible: false,
				pacedUntil: nowMs + R0_SLOW_TIER_BASE_DELAY_MS
			};
		}
	}

	return {
		getOwnership: ownershipOrUndefined,

		async filterEligible(records, nowMs, onPacedUntil) {
			if (!config) return records;
			const at = nowMs ?? now();
			const eligible: TRecord[] = [];
			// Scoped to this call: the next pass must see failures recorded since.
			const passReads: PoisonPassReads = new Map();
			for (const record of records) {
				// A poison-read failure must not let one broken item starve healthy siblings.
				// `guardedVerdict` reports it and answers "not eligible", which keeps the item out of
				// this pass only; a later wake/reload retries the admission read.
				const verdict = await guardedVerdict(record, at, passReads);
				if (verdict.eligible) eligible.push(record);
				else if (typeof verdict.pacedUntil === "number" && Number.isFinite(verdict.pacedUntil)) {
					onPacedUntil?.(verdict.pacedUntil, record);
				}
			}
			return eligible;
		},

		async isPaced(record, nowMs, onPacedUntil) {
			if (!config) return false;
			const verdict = await guardedVerdict(record, nowMs ?? now());
			if (!verdict.eligible && typeof verdict.pacedUntil === "number") onPacedUntil?.(verdict.pacedUntil);
			return !verdict.eligible;
		},

		async recordFailure(record, error, transaction) {
			if (!config) return undefined;
			const ownership = await ownershipOrUndefined();
			// The durable budget is unavailable. Charge the bounded in-memory one instead, so the lane
			// still gets a pacing window back and cannot retry at wake speed forever. No writer fence is
			// needed (or possible) for a per-tab counter, so this deliberately runs without a transaction.
			if (!ownership || typeof ownership.recordPoisonFailure !== "function") return noteMemoryFailure(record, error);
			if (!transaction) return undefined;
			try {
				return await ownership.recordPoisonFailure({
					originKey: config.originKey,
					identity: config.identity(record),
					reason: error instanceof Error ? error.message : String(error),
					ceiling: config.ceiling ?? defaultCeiling(),
					now: now(),
					transaction
				});
			} catch (poisonError) {
				onError("record", record, poisonError);
				// The durable write failed, so this failure would otherwise be uncounted and the item
				// unpaced. Fall back to the in-memory counter for the same reason as above.
				return noteMemoryFailure(record, error);
			}
		},

		async clearFailure(record, transaction) {
			if (!config) return;
			// The in-memory fallbacks are per-tab bookkeeping, not durable state, so a success clears
			// them unconditionally — there is nothing for a writer fence to protect.
			const key = itemKey(record);
			memoryFailures.delete(key);
			legacyPacingStartedAt.delete(key);
			// A poison budget is owned by the same item writer as the state transition.
			// Without the transaction supplied by commitIfWriterOwns there is no safe
			// owner/epoch fence, so never issue a standalone cleanup from a lane.
			if (!transaction) return;
			const ownership = await ownershipOrUndefined();
			try {
				await ownership?.clearPoisonFailure?.(config.originKey, config.identity(record), transaction);
			} catch (error) {
				// Completion is already durable; a best-effort poison-budget cleanup must not
				// turn a successful upload into a retry or Needs Attention outcome.
				onError("clear", record, error);
			}
		}
	};
}
